import { useState } from "react";
export function useScopedCommonTemplatesScopeChangeTracker(): ScopedCommonTemplatesScopeChangeTracker {
    const [changes, setChanges] = useState<Map<ScopedCommonTemplatesScopeKey, CommonTemplatesScope | undefined>>(new Map());
    return {
        has: (key: ScopedCommonTemplatesScopeKey) => changes.has(key),
        get: (key: ScopedCommonTemplatesScopeKey) => {
            if (changes.has(key)) {
                return changes.get(key)?.environmentIds;
            }
            throw new Error(`Key "${key}" not found in changed project tenant variables in scoped common templates value scope tracker.`);
        },
        set: (key: ScopedCommonTemplatesScopeKey, value: string[] | undefined) => {
            const copy = new Map(changes);
            const existingValue = copy.get(key);
            copy.set(key, { environmentIds: value || [], status: existingValue?.status });
            setChanges(copy);
        },
        remove: (key: ScopedCommonTemplatesScopeKey) => {
            const copy = new Map(changes);
            copy.delete(key);
            setChanges(copy);
        },
        add: (key: ScopedCommonTemplatesScopeKey, environmentIds: string[]) => {
            const copy = new Map(changes);
            copy.set(key, { environmentIds, status: "new" });
            setChanges(copy);
        },
        delete: (key: ScopedCommonTemplatesScopeKey) => {
            const templatesValue = changes.get(key);
            const copy = new Map(changes);
            if (templatesValue?.status === "new") {
                copy.delete(key);
            }
            else {
                copy.set(key, { environmentIds: [], status: "deleted" });
            }
            setChanges(copy);
        },
        reset: () => setChanges(new Map()),
        values: () => Array.from(changes.entries()).map(([key, value]) => {
            const { setId, templateId, tenantId, guid } = extractCommonTemplateValueIds(key);
            return {
                setId,
                templateId,
                tenantId,
                guid,
                environmentIds: value?.environmentIds,
                status: value?.status,
            };
        }),
        hasAny: () => changes.size > 0,
        changedTenants: () => {
            const tenantIds: Set<string> = new Set();
            for (const variableKey of changes.keys()) {
                const { tenantId } = extractCommonTemplateValueIds(variableKey);
                tenantIds.add(tenantId);
            }
            return tenantIds;
        },
        newVariables: (setId: string, templateId: string, tenantId: string) => {
            const filteredMap = new Map<ScopedCommonTemplatesScopeKey, string[] | undefined>();
            for (const [key, template] of changes.entries()) {
                if (key.startsWith(`${setId};${templateId};${tenantId}`) && template?.status === "new") {
                    filteredMap.set(key, template.environmentIds);
                }
            }
            return filteredMap;
        },
    };
}
type Status = "new" | "deleted";
export interface CommonTemplatesScope {
    environmentIds: string[];
    status?: Status;
}
export interface ScopedCommonTemplatesScopeChangeTracker {
    has: (key: ScopedCommonTemplatesScopeKey) => boolean;
    get: (key: ScopedCommonTemplatesScopeKey) => string[] | undefined;
    set: (key: ScopedCommonTemplatesScopeKey, scope: string[] | undefined) => void;
    remove: (key: ScopedCommonTemplatesScopeKey) => void;
    add: (key: ScopedCommonTemplatesScopeKey, scope: string[]) => void;
    delete: (key: ScopedCommonTemplatesScopeKey) => void;
    reset: () => void;
    values: () => ChangedCommonTemplatesScope[];
    hasAny: () => boolean;
    changedTenants: () => Set<string>;
    newVariables: (setId: string, templateId: string, tenantId: string) => Map<ScopedCommonTemplatesScopeKey, string[] | undefined>;
}
export interface ChangedCommonTemplatesScope {
    setId: string;
    templateId: string;
    tenantId: string;
    guid: string;
    environmentIds: string[] | undefined;
    status: Status | undefined;
}
export type ScopedCommonTemplatesScopeKey = `${string};${string};${string};${string}`;
export function createScopedCommonTemplatesScopeKey(setId: string, templateId: string, tenantId: string, guid: string): ScopedCommonTemplatesScopeKey {
    return `${setId};${templateId};${tenantId};${guid}`;
}
export function extractCommonTemplateValueIds(key: ScopedCommonTemplatesScopeKey): {
    setId: string;
    templateId: string;
    tenantId: string;
    guid: string;
} {
    const extracted = key.split(`;`);
    return {
        setId: extracted[0],
        templateId: extracted[1],
        tenantId: extracted[2],
        guid: extracted[3],
    };
}
